import React from 'react';
import SensorWidgetWithIcon from '@/comp/common/icons/SensorWidgetWithIcon';
import {
  getWidgetBackgroundColor,
  getWidgetBorderColor,
  getWidgetCircleColor,
} from '../SensorNodeWidget/SensorNodeWidget.utils';
import { DeviceType } from '@/constants/deviceTypeEnum';
import FlowIcon from '@/comp/common/icons/FlowIcon';
import DewPointIcon from '@/comp/common/icons/DewPointIcon';
import PressureIcon from '@/comp/common/icons/PressureIcon';
import AirTemperatureIcon from '@/comp/common/icons/AirTemperatureIcon';
import ComboSensorWidgetWithIcon from '@/comp/common/icons/ComboSensorWidgetWithIcon';
import { CustomThemeToken } from '@/theme';
import { formatDate } from '../../nodes/compressor/CompressorNodeWidget.utils';

export const getDeviceToRender = (
  deviceType: DeviceType,
  isSelected: boolean,
  inMaintenance: boolean,
  renderedOnMap: boolean,
  isConnected: boolean,
  token: CustomThemeToken,
): React.ReactElement | null => {
  const borderColor = getWidgetBorderColor(isSelected, inMaintenance, renderedOnMap, token);
  const backgroundColor = getWidgetBackgroundColor(isSelected, inMaintenance, renderedOnMap, token);
  const circleColor = getWidgetCircleColor(isConnected, token);

  switch (deviceType) {
    case DeviceType.FlowSensor:
      return React.createElement(SensorWidgetWithIcon, {
        width: 50,
        height: 44,
        Icon: React.createElement(FlowIcon, { color: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    case DeviceType.PressureSensor:
      return React.createElement(SensorWidgetWithIcon, {
        width: 50,
        height: 44,
        Icon: React.createElement(PressureIcon, { fillColor: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    case DeviceType.DewpointSensor:
      return React.createElement(SensorWidgetWithIcon, {
        width: 50,
        height: 44,
        Icon: React.createElement(DewPointIcon, { color: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    case DeviceType.AirTemperatureSensor:
      return React.createElement(SensorWidgetWithIcon, {
        width: 50,
        height: 44,
        Icon: React.createElement(AirTemperatureIcon, { fillColor: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    case DeviceType.FlowAndPressureSensor:
      return React.createElement(ComboSensorWidgetWithIcon, {
        width: 83,
        height: 44,
        Icon1: React.createElement(FlowIcon, { color: token.colorIcon }),
        Icon2: React.createElement(PressureIcon, { fillColor: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    case DeviceType.DewpointAndPressureSensor:
      return React.createElement(ComboSensorWidgetWithIcon, {
        width: 83,
        height: 44,
        Icon1: React.createElement(DewPointIcon, { color: token.colorIcon }),
        Icon2: React.createElement(PressureIcon, { fillColor: token.colorIcon }),
        borderColor,
        backgroundColor,
        circleColor: circleColor,
      });
    default:
      return null;
  }
};

export const setPayloadForCreateDevice = (
  deviceType: DeviceType,
  name: string,
  customerId: string,
  siteId: string,
  stationId: string,
  sourceDeviceId: string | string[],
  targetDeviceId: string | string[],
  deviceModelId: string,
  controllerId: string,
  deviceKind: string | null,
) => {
  const basePayload = {
    customerId,
    siteId,
    stationId,
    name,
    sourceDeviceId,
    targetDeviceId,
    deviceModelId,
    controllerId,
    sourceDeviceToDevices: [],
    physicalSchema: {
      elementIndex: 0,
      onDevices: [],
      toDevices: [],
      relatedDevices: [],
      isIndependent: false,
    },
    attributes: {},
    sourceDeviceName: '',
    targetDeviceName: '',
  };

  return {
    ...basePayload,
    deviceType,
    deviceKind,
  };
};

export const setDeviceTypesForModelSpec = (deviceType: DeviceType): string => {
  switch (deviceType) {
    case DeviceType.FlowAndPressureSensor:
      return [DeviceType.FlowSensor, DeviceType.PressureSensor].join(',');
    case DeviceType.DewpointAndPressureSensor:
      return [DeviceType.DewpointSensor, DeviceType.PressureSensor].join(',');
    case DeviceType.AmbientHumiditySensor:
    case DeviceType.AmbientTemperatureSensor:
    case DeviceType.AmbientHumidityAndTemperatureSensor:
      return [
        DeviceType.AmbientHumiditySensor,
        DeviceType.AmbientTemperatureSensor,
        DeviceType.AirTemperatureSensor,
      ].join(',');
    default:
      return deviceType;
  }
};

export const mapToOptions = (data, idField = 'id') =>
  data.map(item => ({
    value: item[idField],
    label: item.name,
    deviceType: item.deviceType,
  }));

export const getLastUpdatedInfo = (lastActivatedUserInfo, t) =>
  `${t('lastUpdated')}: ${formatDate(lastActivatedUserInfo.activateTime)} | ${lastActivatedUserInfo.lastEditor}`;
